<template>
  <div class="hikes-app">
    <!-- -------- Header -------- -->
    <div class="hikes-header d-flex justify-content-between">
      <div>
        <h1 class="display-4 mb-1"><strong>{{ headerTitle }}</strong></h1>
        <p v-if="curatedByText" class="text-muted mb-0"><small>Curated by {{ curatedByText }}</small></p>
      </div>

      <div class="header-actions d-flex gap-2">
        <button
          v-if="!isPublicContext && !selectionMode"
          class="btn btn-sm btn-secondary d-flex align-items-center"
          @click="openImportDialog"
        >
          <i class="material-icons me-2">upload</i>
          Import
        </button>
        <a
          v-if="!isPublicContext && !selectionMode"
          class="btn btn-sm btn-secondary d-flex align-items-center"
          href="/hike-planner"
        >
          <i class="material-icons me-2">add</i>
          New
        </a>
        <button
          v-if="!isPublicContext"
          class="btn btn-sm d-flex align-items-center"
          :class="selectionMode ? 'btn-outline-secondary' : 'btn-secondary'"
          @click="toggleSelectionMode"
        >
          <i class="material-icons me-2">{{ selectionMode ? 'close' : 'delete' }}</i>
          {{ selectionMode ? 'Cancel' : 'Delete' }}
        </button>
        <button
          v-if="!selectionMode"
          class="btn btn-sm btn-secondary d-flex align-items-center justify-content-center rounded-circle p-0"
          style="width:35px;height:35px !important;"
          @click="shareCurrentPage"
        >
          <i class="material-icons" style="font-size:17px">share</i>
        </button>
        
        <!-- -------- View Toggle (Always Visible) -------- -->
        <div class="d-none d-sm-flex justify-content-end mb-3">
          <div class="view-toggle" role="group" aria-label="View toggle">
            <button type="button" :class="['view-toggle-btn', viewMode === 'list' ? 'active' : '']" :aria-pressed="viewMode === 'list'" @click="setViewMode('list')">
              <span class="material-icons" style="font-size:20px">format_list_bulleted</span>
            </button>
            <button type="button" :class="['view-toggle-btn', viewMode === 'map' ? 'active' : '']" :aria-pressed="viewMode === 'map'" @click="setViewMode('map')">
              <span class="material-icons" style="font-size:20px">map</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <hr class="my-3">
    </div>

    <!-- -------- Filters Row -------- -->
    <div v-show="viewMode === 'list'" class="filters-row gap-2 mb-3" :class="{ 'd-flex': viewMode === 'list' }">
      <!-- Clear All Filters Button -->
      <button v-if="hasActiveFilters" class="btn btn-sm btn-filter btn-clear-all" @click="clearAllFilters">
        <i class="material-icons me-1">clear</i>
        Clear All
      </button>
      <div class="dropdown" data-bs-auto-close="outside">
        <button ref="distanceDropdownToggle" class="btn btn-sm btn-filter dropdown-toggle rounded-pill" :class="{ active: isDistanceFilterActive }" type="button" data-bs-toggle="dropdown" data-bs-boundary="viewport">
          Distance <i class="material-icons">expand_more</i>
          <span v-if="isDistanceFilterActive" class="filter-indicator"></span>
        </button>
        <div class="dropdown-menu p-3" style="min-width: 320px;" @click.stop @mousedown.stop>
          <div class="mb-2">
            <template v-if="distanceRangeDraftKm[1] >= distanceMaxBound">
              <small>Min: {{ distanceRangeDraftKm[0] }}{{ distanceUnitSymbol }} · Max: {{ distanceMaxBound }}{{ distanceUnitSymbol }}+</small>
            </template>
            <template v-else>
              <small>Min: {{ distanceRangeDraftKm[0] }}{{ distanceUnitSymbol }} · Max: {{ distanceRangeDraftKm[1] }}{{ distanceUnitSymbol }}</small>
            </template>
          </div>
          <VueSlider
            v-model="distanceRangeDraftKm"
            :min="0"
            :max="distanceMaxBound"
            :interval="1"
            :dot-size="16"
            :height="4"
            :tooltip="'none'"
          />
          <div class="d-flex justify-content-between mt-2">
            <button class="btn btn-sm btn-link p-0" @click="resetDistanceFilter">Clear</button>
            <button class="btn btn-sm btn-primary" @click="applyDistanceFilter">Apply</button>
          </div>
        </div>
      </div>
      <div class="dropdown" data-bs-auto-close="outside">
        <button ref="elevationDropdownToggle" class="btn btn-sm btn-filter dropdown-toggle rounded-pill" :class="{ active: isElevationFilterActive }" type="button" data-bs-toggle="dropdown" data-bs-boundary="viewport">
          Elevation <i class="material-icons">expand_more</i>
          <span v-if="isElevationFilterActive" class="filter-indicator"></span>
        </button>
        <div class="dropdown-menu p-3" style="min-width: 320px;" @click.stop @mousedown.stop>
          <div class="mb-2">
            <template v-if="elevationRangeDraftM[1] >= elevationMaxBound">
              <small>Min: {{ elevationRangeDraftM[0] }}{{ elevationUnitSymbol }} · Max: {{ elevationMaxBound }}{{ elevationUnitSymbol }}+</small>
            </template>
            <template v-else>
              <small>Min: {{ elevationRangeDraftM[0] }}{{ elevationUnitSymbol }} · Max: {{ elevationRangeDraftM[1] }}{{ elevationUnitSymbol }}</small>
            </template>
          </div>
          <VueSlider
            v-model="elevationRangeDraftM"
            :min="0"
            :max="elevationMaxBound"
            :interval="50"
            :dot-size="16"
            :height="4"
            :tooltip="'none'"
          />
          <div class="d-flex justify-content-between mt-2">
            <button class="btn btn-sm btn-link p-0" @click="resetElevationFilter">Clear</button>
            <button class="btn btn-sm btn-primary" @click="applyElevationFilter">Apply</button>
          </div>
        </div>
      </div>
      <div class="dropdown" data-bs-auto-close="outside">
        <button ref="difficultyDropdownToggle" class="btn btn-sm btn-filter dropdown-toggle rounded-pill" :class="{ active: isDifficultyFilterActive }" type="button" data-bs-toggle="dropdown" data-bs-boundary="viewport">
          Difficulty <i class="material-icons">expand_more</i>
          <span v-if="isDifficultyFilterActive" class="filter-indicator"></span>
        </button>
        <div class="dropdown-menu p-3" style="min-width: 260px;" @click.stop @mousedown.stop>
          <div class="mb-2">
            <small>Select one or more</small>
          </div>
          <div class="mb-2" v-for="opt in DIFFICULTY_OPTIONS" :key="opt">
            <label class="d-flex align-items-center gap-2">
              <input type="checkbox" :value="opt" v-model="difficultyDraft" />
              <span>{{ opt }}</span>
            </label>
          </div>
          <div class="d-flex justify-content-between mt-2">
            <button class="btn btn-sm btn-link p-0" @click="resetDifficultyFilter">Clear</button>
            <button class="btn btn-sm btn-primary" @click="applyDifficultyFilter">Apply</button>
          </div>
        </div>
      </div>
      <div class="dropdown" data-bs-auto-close="outside">
        <button ref="routeTypeDropdownToggle" class="btn btn-sm btn-filter dropdown-toggle rounded-pill" :class="{ active: isRouteTypeFilterActive }" type="button" data-bs-toggle="dropdown" data-bs-boundary="viewport">
          {{ routeTypeButtonLabel }} <i class="material-icons">expand_more</i>
          <span v-if="isRouteTypeFilterActive" class="filter-indicator"></span>
        </button>
        <div class="dropdown-menu p-3" style="min-width: 260px;" @click.stop @mousedown.stop>
          <div class="mb-2">
            <small>Select a type</small>
          </div>
          <div class="mb-2">
            <label class="d-flex align-items-center gap-2">
              <input type="radio" name="routeType" :value="null" v-model="routeTypeDraft" />
              <span>All types</span>
            </label>
          </div>
          <div class="mb-2" v-for="opt in ROUTE_TYPE_OPTIONS" :key="opt.value">
            <label class="d-flex align-items-center gap-2">
              <input type="radio" name="routeType" :value="opt.value" v-model="routeTypeDraft" />
              <span>{{ opt.label }}</span>
            </label>
          </div>
          <div class="d-flex justify-content-between mt-2">
            <button class="btn btn-sm btn-link p-0" @click="resetRouteTypeFilter">Clear</button>
            <button class="btn btn-sm btn-primary" @click="applyRouteTypeFilter">Apply</button>
          </div>
        </div>
      </div>
      <div class="dropdown" data-bs-auto-close="outside">
        <button ref="featureTagDropdownToggle" class="btn btn-sm btn-filter dropdown-toggle rounded-pill" :class="{ active: isFeatureTagFilterActive }" type="button" data-bs-toggle="dropdown" data-bs-boundary="viewport">
          {{ featureTagButtonLabel }} <i class="material-icons">expand_more</i>
          <span v-if="isFeatureTagFilterActive" class="filter-indicator"></span>
        </button>
        <div class="dropdown-menu p-3" style="min-width: 260px;" @click.stop @mousedown.stop>
          <div class="mb-2">
            <small>Select a feature tag</small>
          </div>
          <div class="mb-2">
            <select class="form-select" v-model="featureTagDraft">
              <option :value="null">All features</option>
              <option v-for="tag in featureTags" :key="tag" :value="tag">{{ formatTagLabel(tag) }}</option>
            </select>
          </div>
          <div class="d-flex justify-content-between mt-2">
            <button class="btn btn-sm btn-link p-0" @click="resetFeatureTagFilter">Clear</button>
            <button class="btn btn-sm btn-primary" @click="applyFeatureTagFilter">Apply</button>
          </div>
        </div>
      </div>
      <div class="dropdown" data-bs-auto-close="outside">
        <button ref="activityTagDropdownToggle" class="btn btn-sm btn-filter dropdown-toggle rounded-pill" :class="{ active: isActivityTagFilterActive }" type="button" data-bs-toggle="dropdown" data-bs-boundary="viewport">
          {{ activityTagButtonLabel }} <i class="material-icons">expand_more</i>
          <span v-if="isActivityTagFilterActive" class="filter-indicator"></span>
        </button>
        <div class="dropdown-menu p-3" style="min-width: 260px;" @click.stop @mousedown.stop>
          <div class="mb-2">
            <small>Select an activity tag</small>
          </div>
          <div class="mb-2">
            <select class="form-select" v-model="activityTagDraft">
              <option :value="null">All activities</option>
              <option v-for="tag in activityTags" :key="tag" :value="tag">{{ formatTagLabel(tag) }}</option>
            </select>
          </div>
          <div class="d-flex justify-content-between mt-2">
            <button class="btn btn-sm btn-link p-0" @click="resetActivityTagFilter">Clear</button>
            <button class="btn btn-sm btn-primary" @click="applyActivityTagFilter">Apply</button>
          </div>
        </div>
      </div>
      <div v-if="customTags.length" class="dropdown" data-bs-auto-close="outside">
        <button ref="customTagDropdownToggle" class="btn btn-sm btn-filter dropdown-toggle rounded-pill" :class="{ active: isCustomTagFilterActive }" type="button" data-bs-toggle="dropdown" data-bs-boundary="viewport">
          {{ customTagButtonLabel }} <i class="material-icons">expand_more</i>
          <span v-if="isCustomTagFilterActive" class="filter-indicator"></span>
        </button>
        <div class="dropdown-menu p-3" style="min-width: 260px;" @click.stop @mousedown.stop>
          <div class="mb-2">
            <small>Select a tag</small>
          </div>
          <div class="mb-2">
            <select class="form-select" v-model="customTagDraft">
              <option :value="null">All tags</option>
              <option v-for="tag in customTags" :key="tag" :value="tag">{{ tag }}</option>
            </select>
          </div>
          <div class="d-flex justify-content-between mt-2">
            <button class="btn btn-sm btn-link p-0" @click="resetCustomTagFilter">Clear</button>
            <button class="btn btn-sm btn-primary" @click="applyCustomTagFilter">Apply</button>
          </div>
        </div>
      </div>
      <div class="dropdown" data-bs-auto-close="outside">
        <button ref="dateDropdownToggle" class="btn btn-sm btn-filter dropdown-toggle rounded-pill" :class="{ active: isDateFilterActive }" type="button" data-bs-toggle="dropdown" data-bs-boundary="viewport">
          Date <i class="material-icons">expand_more</i>
          <span v-if="isDateFilterActive" class="filter-indicator"></span>
        </button>
        <div class="dropdown-menu p-3" style="min-width: 320px;" @click.stop @mousedown.stop>
          <div class="mb-2">
            <small>Created between</small>
          </div>
          <div class="d-flex gap-2 align-items-center">
            <input class="form-control" type="date" v-model="startDateDraft" />
            <span>–</span>
            <input class="form-control" type="date" v-model="endDateDraft" />
          </div>
          <div class="d-flex justify-content-between mt-2">
            <button class="btn btn-sm btn-link p-0" @click="resetDateFilter">Clear</button>
            <button class="btn btn-sm btn-primary" @click="applyDateFilter">Apply</button>
          </div>
        </div>
      </div>
      <div class="dropdown" data-bs-auto-close="outside">
        <button ref="sortDropdownToggle" class="btn btn-sm btn-filter dropdown-toggle rounded-pill" :class="{ active: isSortFilterActive }" type="button" data-bs-toggle="dropdown" data-bs-boundary="viewport">
          Sort by <i class="material-icons">expand_more</i>
          <span v-if="isSortFilterActive" class="filter-indicator"></span>
        </button>
        <div class="dropdown-menu p-3" style="min-width: 260px;" @click.stop @mousedown.stop>
          <div class="mb-2">
            <small>Select sort</small>
          </div>
          <div class="mb-2">
            <label class="d-flex align-items-center gap-2">
              <input type="radio" name="sort" value="name_asc" v-model="sortDraft" />
              <span>Name (A–Z)</span>
            </label>
          </div>
          <div class="mb-2">
            <label class="d-flex align-items-center gap-2">
              <input type="radio" name="sort" value="created_desc" v-model="sortDraft" />
              <span>Created date (newest first)</span>
            </label>
          </div>
          <div class="mb-2">
            <label class="d-flex align-items-center gap-2">
              <input type="radio" name="sort" value="distance_desc" v-model="sortDraft" />
              <span>Distance (longest first)</span>
            </label>
          </div>
          <div class="mb-2">
            <label class="d-flex align-items-center gap-2">
              <input type="radio" name="sort" value="distance_asc" v-model="sortDraft" />
              <span>Distance (shortest first)</span>
            </label>
          </div>
          <div class="d-flex justify-content-between mt-2">
            <button class="btn btn-sm btn-link p-0" @click="resetSortFilter">Reset</button>
            <button class="btn btn-sm btn-primary" @click="applySortFilter">Apply</button>
          </div>
        </div>
      </div>
      <div class="dropdown" data-bs-auto-close="outside">
        <button ref="hideCompletedDropdownToggle" class="btn btn-sm btn-filter dropdown-toggle rounded-pill" :class="{ active: isHideCompletedFilterActive }" type="button" data-bs-toggle="dropdown" data-bs-boundary="viewport">
          Hide completed <i class="material-icons">expand_more</i>
          <span v-if="isHideCompletedFilterActive" class="filter-indicator"></span>
        </button>
        <div class="dropdown-menu p-3" style="min-width: 240px;" @click.stop @mousedown.stop>
          <label class="d-flex align-items-center gap-2">
            <input type="checkbox" v-model="hideCompletedHikesDraft" />
            <span>Hide completed</span>
          </label>
          <div class="d-flex justify-content-between mt-2">
            <button class="btn btn-sm btn-link p-0" @click="resetHideCompletedFilter">Reset</button>
            <button class="btn btn-sm btn-primary" @click="applyHideCompletedFilter">Apply</button>
          </div>
        </div>
      </div>
    </div>

    <!-- -------- Search Row -------- -->
    <div v-show="viewMode === 'list'" class="search-row gap-3 mb-4" :class="{ 'd-flex': viewMode === 'list' }">
      <div class="search-input-wrapper flex-grow-1">
        <i class="material-icons search-icon">search</i>
        <input class="form-control search-input" type="text" v-model="searchString" @keyup.enter="searchTrails(true)" placeholder="Search by name" />
      </div>
      <button class="btn btn-primary" @click="searchTrails(true)">Search</button>
    </div>

    <!-- -------- List -------- -->
    <div v-show="viewMode === 'list'" class="hikes-list">
      <div v-if="trails.length" class="hikes-items">
        <HikeItem
          v-for="(trail,index) in trails"
          :key="trail.id || index"
          :trail="trail"
          :is-public="isPublicContext"
          :is-tour-provider="isTourProvider"
          :is-trails-user="isTrailsUser"
          :selection-mode="selectionMode"
          :is-selected="selectedHikeIds.has(trail.id)"
          @show-flyover="showFlyover"
          @edit-hike="editHike"
          @edit-details="openEditDetails"
          @export-gpx="exportGpx"
          @copy-share="triggerCopyModel"
          @add-to-list="launchAddToList"
          @duplicate-hike="duplicateHike"
          @destroy-hike="destroyHike"
          @remove-official="removeOfficialStatus"
          @submit-official="submitHikeForOfficial"
          @selection-changed="toggleHikeSelection"
        />
      </div>
      <div v-else class="no-trails">
        <p>No hikes found.</p>
      </div>

      <div v-if="hasMoreTrails" class="see-more d-flex justify-content-center m-0">
        <button class="btn btn-link" @click="searchTrailsClicked($event, false)">See More</button>
      </div>

      <!-- Bulk Delete Floating Action Bar -->
      <Transition name="slide-up">
        <div v-if="selectionMode && !isPublicContext" class="bulk-action-bar">
          <div class="bulk-action-content">
            <div class="selection-info">
              <span v-if="selectedHikeIds.size === 0" class="text-muted">Select hikes to delete</span>
              <span v-else>{{ selectedHikeIds.size }} hike{{ selectedHikeIds.size !== 1 ? 's' : '' }} selected</span>
            </div>
            <div class="bulk-actions d-flex gap-2">
              <button
                v-if="selectedHikeIds.size > 0 && selectedHikeIds.size < trails.length"
                class="btn btn-sm btn-link"
                @click="selectAllHikes"
              >
                Select all
              </button>
              <button
                v-if="selectedHikeIds.size === trails.length && trails.length > 0"
                class="btn btn-sm btn-link"
                @click="deselectAllHikes"
              >
                Deselect all
              </button>
              <button
                class="btn btn-sm btn-secondary"
                @click="toggleSelectionMode"
              >
                Cancel
              </button>
              <button
                class="btn btn-sm btn-danger"
                :disabled="selectedHikeIds.size === 0 || bulkDeleteInProgress"
                @click="bulkDeleteHikes"
              >
                <span v-if="bulkDeleteInProgress" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                {{ bulkDeleteInProgress ? 'Deleting...' : `Delete ${selectedHikeIds.size || ''}` }}
              </button>
            </div>
          </div>
        </div>
      </Transition>

      <LoadingDialog :show-loading-dialog="loading" />
      <CopyLinkModal ref="shareModal" helper-text="Copy the link to share this page" />
    </div>
    
    <!-- -------- Map View -------- -->
    <div v-show="viewMode === 'map'" class="hikes-map">
      <link href='https://api.mapbox.com/mapbox-gl-js/v3.10.0/mapbox-gl.css' rel='stylesheet' />
      <div id="hikes-map" class="hikes-map-container">
        <!-- Selected Hike Overlay -->
        <div v-if="selectedHike || selectedHikeLoading" class="hike-overlay">
          <div v-if="selectedHikeLoading" class="hike-overlay-loading">Loading…</div>
          <div v-else class="hike-overlay-card">
            <button class="overlay-close" @click="selectedHike = null" aria-label="Close">×</button>
            <HikeItem
              :trail="selectedHike"
              :is-public="isPublicContext"
              :is-tour-provider="isTourProvider"
              :is-trails-user="isTrailsUser"
              :selection-mode="false"
              :is-selected="false"
              @show-flyover="showFlyover"
              @edit-hike="editHike"
              @edit-details="openEditDetails"
              @export-gpx="exportGpx"
              @copy-share="triggerCopyModel"
              @request-download="openDownloadForHike"
              @add-to-list="launchAddToList"
              @duplicate-hike="duplicateHike"
              @destroy-hike="destroyHike"
              @remove-official="removeOfficialStatus"
              @submit-official="submitHikeForOfficial"
              @selection-changed="toggleHikeSelection"
            />
          </div>
        </div>
      </div>
    </div>
    <!-- Edit Details Modal -->
    <EditHikeDetailsModal
      v-if="!isPublicContext"
      ref="editDetailsModal"
      :hike="editDetailsHike"
      @save="saveHikeDetails"
    />

    <!-- Download Modal Mount -->
    <div id="download_modal"></div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, nextTick, onBeforeUnmount, computed } from 'vue'
import graph_client from '../../utils/graphql/graph_client'
import { Dropdown, Modal } from 'bootstrap'
import moment from 'moment/moment'
import { MeasurementPrefUtility } from '../../utils/helpers/MeasurementPref'
import { HikeTimeUtility, PrettyTime } from '../../utils/helpers/HikeTime'
import VueSlider from 'vue-slider-component'
import 'vue-slider-component/dist-css/vue-slider-component.css'
import mapboxgl from 'mapbox-gl'
import { HiikerMapStyle } from '../../utils/models/MapStyles'
import { LayerRegistry } from '../../utils/layer-registry'
import emitter from '../../utils/bus'

import LoadingDialog from '../../utils/shared_components/LoadingDialog.vue'
import CopyLinkModal from '../../utils/shared_components/CopyLinkModal.vue'
import HikeItem from './components/HikeItem.vue'
import EditHikeDetailsModal from './components/EditHikeDetailsModal.vue'
import {
  SearchHikes,
  GetHike,
  GetHikeExplore,
  DestroyHike,
  DestroyUserHikes,
  DuplicateHike,
  UpdateHikeWithPlannedTrails,
  UpdateHikeDetails,
  SubmitHikeForOfficial
} from '../../utils/graphql/hikes-graph'
import { SearchPublicHikes } from '../../utils/graphql/public-hikes-graph'
import { GetAllHikeTags, GetUserCustomTags } from '../../utils/graphql/hike-tags-graph'
import { geojsonToGpx } from '../../utils/geojsonToGpx'
import { LineSourceHelper } from '../base_map_dialog/helpers/LineSourceHelper'
import { darkenColor } from '../base_map_dialog/helpers/BaseMapDialogHelpers'
import { qrCodeLinkForResource } from '../../utils/helpers/DynamicLinkHelper'
import { OnBoardingGoal } from '../../utils/models/OnBoardingGoal'

/* ---------- Context Configuration ---------- */
const rootElement = document.getElementById('hikes') || document.getElementById('public-hikes-app')
let parsedConfig = {}
if (rootElement && rootElement.dataset && rootElement.dataset.appConfig) {
  try {
    parsedConfig = JSON.parse(rootElement.dataset.appConfig)
  } catch (err) {
    console.warn('Failed to parse hikes app config dataset:', err)
  }
}

const isPublicContext = parsedConfig?.context === 'public'
const publicFirebaseId = parsedConfig?.firebaseUserId || null
const headerTitle = computed(() => (isPublicContext ? 'Hikes' : 'My hikes'))
const curatorDisplayName = computed(() => {
  const rawName = isPublicContext
    ? parsedConfig?.displayName
    : window.user?.display_name ?? window.user?.displayName ?? window.user?.title ?? window.user?.name
  return typeof rawName === 'string' ? rawName.trim() : ''
})

const curatorUsername = computed(() => {
  const rawUsername = isPublicContext
    ? parsedConfig?.displayName
    : window.user?.displayName
  return typeof rawUsername === 'string' ? rawUsername.trim() : ''
})

const curatedByText = computed(() => {
  const display = curatorDisplayName.value
  const username = curatorUsername.value
  if (display && username) {
    if (display.replace(/^@/, '').toLowerCase() === username.toLowerCase()) {
      return `${username}`
    }
    return `${display} (${username})`
  }
  if (display) return display
  if (username) return `${username}`
  return isPublicContext ? '' : 'you'
})

// Tour provider status - make it reactive so child components can use it
const isTourProvider = computed(() => {
  if (!window.user) return false
  return Array.isArray(window.user.tour_providers) && window.user.tour_providers.length > 0
})

// Trails user status - only trails@hiiker.co can manage official status
const isTrailsUser = computed(() => window.currentUserEmail === 'trails@hiiker.co')

/* ---------- Reactive State ---------- */
const trails        = ref([])
const searchString  = ref('')
// Created date range (YYYY-MM-DD strings from <input type="date">)
const startDateApplied = ref(null)
const endDateApplied   = ref(null)
const startDateDraft   = ref(null)
const endDateDraft     = ref(null)
const currentPage   = ref(1)
const pageSize      = ref(10)
const hasMoreTrails = ref(false)
const loading       = ref(true)
const viewMode      = ref('list') // 'list' | 'map'
const shareModal    = ref(null)
// Processing status polling
const processingHikeIds = ref(new Set())
let statusPollingInterval = null
// Distance/elevation sliders work in the user's units; the GraphQL query gets
// metres (see searchTrails). Bounds = 200km / 5000m expressed in the user's
// units; reaching the bound means "no max". Ref names keep their old suffix
// but now hold user-unit values.
const measurementPref = MeasurementPrefUtility.preference()
const distanceMaxBound = Math.round(measurementPref.bigUnitValue(200))
const elevationMaxBound = Math.round(measurementPref.smallUnitValue(5000))
const distanceUnitSymbol = measurementPref.kilometerSymbol()
const elevationUnitSymbol = measurementPref.meterSymbol()
const distanceRangeAppliedKm = ref([0, distanceMaxBound])
const distanceRangeDraftKm   = ref([0, distanceMaxBound])
const elevationRangeAppliedM = ref([0, elevationMaxBound])
const elevationRangeDraftM   = ref([0, elevationMaxBound])
let dropdownInstances = []
const distanceDropdownToggle = ref(null)
const elevationDropdownToggle = ref(null)
const difficultyDropdownToggle = ref(null)
const dateDropdownToggle = ref(null)
const sortDropdownToggle = ref(null)
const featureTagDropdownToggle = ref(null)
const activityTagDropdownToggle = ref(null)
const customTagDropdownToggle = ref(null)

// Hike overlay state
const selectedHike = ref(null)
const selectedHikeLoading = ref(false)

// Sort state: 'created_desc' (default), 'name_asc', 'distance_desc', 'distance_asc'
const sortApplied = ref('created_desc')
const sortDraft   = ref('created_desc')
// Hide hikes the user has already completed. Enabled by default (user-hikes view only).
const hideCompletedHikesApplied = ref(true)
const hideCompletedHikesDraft   = ref(true)
const hideCompletedDropdownToggle = ref(null)
const DIFFICULTY_OPTIONS = ['Easy', 'Medium', 'Difficult', 'Extra Difficult']
const difficultyApplied = ref([])
const difficultyDraft   = ref([])

// Route type filter. Values match the backend `trail_type` column (and the
// iOS/Android apps' TypeSense strings); labels mirror the mobile filter UI.
const ROUTE_TYPE_OPTIONS = [
  { value: 'point_to_point', label: 'Point to Point' },
  { value: 'loop', label: 'Loop' },
  { value: 'out_and_back', label: 'Out and Back' },
  { value: 'multi_day', label: 'Multi Day' }
]
const routeTypeDropdownToggle = ref(null)
const routeTypeApplied = ref(null)
const routeTypeDraft   = ref(null)
const routeTypeButtonLabel = computed(() => {
  const opt = ROUTE_TYPE_OPTIONS.find(o => o.value === routeTypeApplied.value)
  return opt ? `Type: ${opt.label}` : 'Type'
})

// Bulk delete / selection mode state
const selectionMode = ref(false)
const selectedHikeIds = ref(new Set())
const bulkDeleteInProgress = ref(false)
const featureTags = ref([])
const activityTags = ref([])
const customTags = ref([])
const featureTagApplied = ref(null)
const featureTagDraft = ref(null)
const activityTagApplied = ref(null)
const activityTagDraft = ref(null)
const customTagApplied = ref(null)
const customTagDraft = ref(null)
const featureTagButtonLabel = computed(() => featureTagApplied.value ? `Feature: ${formatTagLabel(featureTagApplied.value)}` : 'Features')
const activityTagButtonLabel = computed(() => activityTagApplied.value ? `Activity: ${formatTagLabel(activityTagApplied.value)}` : 'Activities')
const customTagButtonLabel = computed(() => customTagApplied.value ? `Tag: ${customTagApplied.value}` : 'Tags')

/* ---------- Filter Active States ---------- */
const isDistanceFilterActive = computed(() => {
  return distanceRangeAppliedKm.value[0] !== 0 || distanceRangeAppliedKm.value[1] !== distanceMaxBound
})

const isElevationFilterActive = computed(() => {
  return elevationRangeAppliedM.value[0] !== 0 || elevationRangeAppliedM.value[1] !== elevationMaxBound
})

const isDifficultyFilterActive = computed(() => {
  return difficultyApplied.value.length > 0
})

const isRouteTypeFilterActive = computed(() => {
  return routeTypeApplied.value !== null
})

const isFeatureTagFilterActive = computed(() => {
  return featureTagApplied.value !== null
})

const isCustomTagFilterActive = computed(() => {
  return customTagApplied.value !== null
})

const isActivityTagFilterActive = computed(() => {
  return activityTagApplied.value !== null
})

const isDateFilterActive = computed(() => {
  return startDateApplied.value !== null || endDateApplied.value !== null
})

const isSortFilterActive = computed(() => {
  return sortApplied.value !== 'created_desc'
})

// Default is to hide completed hikes, so the filter only reads as "active"
// (non-default) when the user has chosen to show completed hikes.
const isHideCompletedFilterActive = computed(() => {
  return hideCompletedHikesApplied.value === false
})

const hasActiveFilters = computed(() => {
  return isDistanceFilterActive.value || isElevationFilterActive.value ||
         isDifficultyFilterActive.value || isRouteTypeFilterActive.value ||
         isFeatureTagFilterActive.value ||
         isActivityTagFilterActive.value || isCustomTagFilterActive.value ||
         isDateFilterActive.value ||
         isSortFilterActive.value || isHideCompletedFilterActive.value ||
         searchString.value.trim() !== ''
})

/* ---------- Lifecycle ---------- */
onMounted(() => {
  document.title = isPublicContext ? 'Public hikes' : 'Hikes'
  searchTrails(true)
  // Mount the download modal app if present
  try { import('../download_modal/main') } catch (e) {}
  loadHikeTags()
})

onBeforeUnmount(() => {
  // Clean up status polling
  stopStatusPolling()

  dropdownInstances.forEach(d => d.dispose())
  dropdownInstances = []
  if (cleanupLineSourceListeners) {
    cleanupLineSourceListeners()
    cleanupLineSourceListeners = null
  }
  removeSelectedHikeLine()
  if (hikesMap) {
    try { hikesMap.remove() } catch (e) {}
    hikesMap = null
  }
})

/* ---------- View Toggle / Map ---------- */
let hikesMap = null
let cleanupLineSourceListeners = null

function setViewMode (mode) {
  if (viewMode.value === mode) return
  console.log('Setting viewMode to:', mode)
  viewMode.value = mode
  console.log('viewMode is now:', viewMode.value)
}

watch(viewMode, (val) => {
  if (val === 'map') {
    nextTick(initMapIfNeeded)
  }
})

// Remove highlight when selection cleared
watch(() => selectedHike.value, (val) => {
  if (!val) removeSelectedHikeLine()
})

function initMapIfNeeded () {
  if (hikesMap) { try { hikesMap.resize() } catch(e) {} return }
  try {
    mapboxgl.accessToken = 'pk.eyJ1IjoiaGlpa2VyLW1hcHMiLCJhIjoiY2x0YThyZHppMWhmejJqbzExODBuY3dveiJ9.nBrtnXgbzxpAx_953HCB-w'
    const style = HiikerMapStyle.standard().styleUrl
    
    hikesMap = new mapboxgl.Map({
      container: 'hikes-map',
      style,
      center: window.lonlat || [0,0],
      zoom: window.lonlat ? 9 : 2
    })

    // Expose for overlay modules
    window.map = hikesMap
    window.layerRegistry = new LayerRegistry(window.map)

    hikesMap.on('load', () => {
      emitter.emit('map-loaded')
      if (!isPublicContext) {
        LineSourceHelper.toggle(
          'get_user_planned_trails',
          true,
          5,
          "#F57700"
        )
        attachLineSourceClickHandlers('get_user_planned_trails')
      }
    })
    hikesMap.on('style.load', () => {
      emitter.emit('map-style-loaded')
      if (!isPublicContext) {
        LineSourceHelper.toggle('get_user_planned_trails', true, 3, "#F57700")
        attachLineSourceClickHandlers('get_user_planned_trails')
      }
      if (selectedHike.value?.geojsonRoutesOnlyString) {
        try { addSelectedHikeLine(JSON.parse(selectedHike.value.geojsonRoutesOnlyString)) } catch(e) {}
      }
    })
  } catch (err) {
    console.error('Error initializing map:', err)
  }
}

function attachLineSourceClickHandlers (srcId) {
  if (isPublicContext) return
  if (!window.map) return

  // Remove prior listeners for safety
  if (cleanupLineSourceListeners) {
    cleanupLineSourceListeners()
    cleanupLineSourceListeners = null
  }

  const mainLayerId = srcId
  const borderLayerId = `${srcId}-border`

  const onClick = (e) => {
    const features = e?.features || []
    const f = features[0]
    // Fallback: if added via map.on('click', layerId), e.features should exist; otherwise queryRenderedFeatures
    let feature = f
    if (!feature) {
      const feats = window.map.queryRenderedFeatures(e.point, { layers: [mainLayerId, borderLayerId] })
      feature = feats && feats[0]
    }
    if (!feature) return

    const props = feature.properties || {}
    const hikeId = props.hike_id || props.hikeId || props.id
    if (hikeId) {
      fetchAndShowHike(`${hikeId}`)
    }
  }

  const onEnter = () => { try { window.map.getCanvas().style.cursor = 'pointer' } catch(e) {} }
  const onLeave = () => { try { window.map.getCanvas().style.cursor = '' } catch(e) {} }

  // Guard: only bind to layers that currently exist
  const layersToBind = [mainLayerId, borderLayerId].filter(id => window.map.getLayer(id))

  layersToBind.forEach(id => {
    window.map.on('click', id, onClick)
    window.map.on('mouseenter', id, onEnter)
    window.map.on('mouseleave', id, onLeave)
  })

  // Background click clears selection if clicking on nothing relevant
  const onBackgroundClick = (e) => {
    const feats = window.map.queryRenderedFeatures(e.point, { layers: [mainLayerId, borderLayerId] })
    if (!feats || feats.length === 0) {
      clearSelectedHike()
    }
  }
  window.map.on('click', onBackgroundClick)

  cleanupLineSourceListeners = () => {
    layersToBind.forEach(id => {
      try { window.map.off('click', id, onClick) } catch(e) {}
      try { window.map.off('mouseenter', id, onEnter) } catch(e) {}
      try { window.map.off('mouseleave', id, onLeave) } catch(e) {}
    })
    try { window.map.off('click', onBackgroundClick) } catch (e) {}
  }
}

async function fetchAndShowHike (id) {
  try {
    selectedHikeLoading.value = true
    const { hike } = await graph_client.request(GetHikeExplore, { id })
    selectedHike.value = hike
    // Add highlighted line for the selected hike
    try {
      if (hike?.geojsonRoutesOnlyString) {
        addSelectedHikeLine(JSON.parse(hike.geojsonRoutesOnlyString))
      }
    } catch (e) {
      console.warn('Failed to render selected hike line', e)
    }
  } catch (err) {
    console.error('Error fetching hike for overlay:', err)
    selectedHike.value = null
    removeSelectedHikeLine()
  } finally {
    selectedHikeLoading.value = false
  }
}

// Selected hike highlight layers
const SELECTED_SRC_ID = 'selected-hike-source'
const SELECTED_MAIN_ID = 'selected-hike-line'
const SELECTED_BORDER_ID = 'selected-hike-line-border'

function removeSelectedHikeLine () {
  if (!window.map) return
  try { if (window.map.getLayer(SELECTED_BORDER_ID)) window.map.removeLayer(SELECTED_BORDER_ID) } catch(e) {}
  try { if (window.map.getLayer(SELECTED_MAIN_ID)) window.map.removeLayer(SELECTED_MAIN_ID) } catch(e) {}
  try { if (window.map.getSource(SELECTED_SRC_ID)) window.map.removeSource(SELECTED_SRC_ID) } catch(e) {}
}

function addSelectedHikeLine (geojson) {
  if (!window.map) return
  removeSelectedHikeLine()
  const color = '#FBCD01'
  const border = darkenColor(color)
  try {
    window.map.addSource(SELECTED_SRC_ID, { type: 'geojson', data: geojson })
    window.map.addLayer({
      id: SELECTED_BORDER_ID,
      type: 'line',
      source: SELECTED_SRC_ID,
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: { 'line-color': border, 'line-width': ['interpolate',['linear'],['zoom'],0,6,14,12] }
    })
    window.map.addLayer({
      id: SELECTED_MAIN_ID,
      type: 'line',
      source: SELECTED_SRC_ID,
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: { 'line-color': color, 'line-width': ['interpolate',['linear'],['zoom'],0,3,14,9] }
    })
  } catch (e) {
    console.error('Error adding selected hike line:', e)
  }
}

function clearSelectedHike () {
  selectedHike.value = null
  removeSelectedHikeLine()
}

async function openDownloadForHike (trail) {
  try {
    console.debug('openDownloadForHike()', trail?.id || trail?.slug)
    // Ensure modal app is mounted
    if (!(window.openDownloadModal)) {
      if (!document.getElementById('download_modal')) {
        const el = document.createElement('div');
        el.id = 'download_modal';
        document.body.appendChild(el);
      }
      try {
        await import('../download_modal/main');
      } catch (e) {
        console.error('Failed to mount download modal app:', e)
      }
    }

    // Provide context for export tab if needed
    if (trail?.mainCountry?.slug && trail?.mainRegion?.slug && trail?.slug) {
      window.country_slug = trail.mainCountry.slug
      window.region_slug = trail.mainRegion.slug
      window.slug = trail.slug
    }

    // Provide QR code LinkForty link dynamically via helper
    if (trail?.id) {
      window.qrCodeUrl = await qrCodeLinkForResource({ type: 'Hike', id: trail.id, fallbackUrl: trail.shareableUrl })
    }

    if (window.openDownloadModal) window.openDownloadModal();
  } catch (err) {
    console.error('Error opening download modal for hike:', err)
  }
}

/* ---------- Utilities ---------- */
function formatDate (date) {
  return moment(date).format('LL')
}

function initializeDropdowns () {
  dropdownInstances.forEach(d => d.dispose())
  dropdownInstances = []
  const list = document.querySelectorAll('[data-bs-toggle="dropdown"]')
  list.forEach(el => dropdownInstances.push(
    Dropdown.getOrCreateInstance(el, {
      popperConfig: {
        // Use fixed positioning so menus aren’t clipped by scroll containers
        strategy: 'fixed',
        modifiers: [
          { name: 'computeStyles', options: { adaptive: false } },
          { name: 'preventOverflow', options: { boundary: 'viewport', altBoundary: true, tether: false } },
        ]
      }
    })
  ))
}

/* ---------- Measurement Formatting ---------- */
function formatDistance (trail) {
  const value = trail?.distanceMeters
  if (!value && value !== 0) return ''
  return MeasurementPrefUtility.preference().smallUnitFormatter(value, true)
}

function formatElevation (trail) {
  const value = trail?.elevationGainMeters
  if (!value && value !== 0) return ''
  return MeasurementPrefUtility.preference().smallUnitFormatter(value, false)
}

async function loadHikeTags () {
  try {
    const { allHikeTags } = await graph_client.request(GetAllHikeTags)
    featureTags.value = Array.isArray(allHikeTags?.featureTags) ? allHikeTags.featureTags : []
    activityTags.value = Array.isArray(allHikeTags?.activityTags) ? allHikeTags.activityTags : []
  } catch (err) {
    console.error('Error loading hike tags:', err)
  }

  try {
    const { userCustomTags } = await graph_client.request(GetUserCustomTags)
    customTags.value = Array.isArray(userCustomTags) ? userCustomTags : []
    // The Tags dropdown is v-if'd on customTags, so it isn't in the DOM during the
    // initial initializeDropdowns() pass — re-init now that it's rendered, otherwise
    // it has no Bootstrap Dropdown instance and tapping it does nothing.
    if (customTags.value.length) nextTick(initializeDropdowns)
  } catch (err) {
    console.error('Error loading user custom tags:', err)
  }
}

function formatTagLabel (tag) {
  if (!tag) return ''
  return tag
    .split('-')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

function resetCustomTagFilter () {
  customTagDraft.value = null
  applyCustomTagFilter()
}

function applyCustomTagFilter () {
  customTagApplied.value = customTagDraft.value
  searchTrails(true)
  nextTick(() => {
    try {
      if (customTagDropdownToggle?.value) {
        Dropdown.getOrCreateInstance(customTagDropdownToggle.value).hide()
      }
    } catch (e) { /* noop */ }
  })
}

function resetFeatureTagFilter () {
  featureTagDraft.value = null
  applyFeatureTagFilter()
}

function applyFeatureTagFilter () {
  featureTagApplied.value = featureTagDraft.value
  searchTrails(true)
  nextTick(() => {
    try {
      if (featureTagDropdownToggle?.value) {
        Dropdown.getOrCreateInstance(featureTagDropdownToggle.value).hide()
      }
    } catch (e) { /* noop */ }
  })
}

function onFeatureTagDropdownShow () {
  featureTagDraft.value = featureTagApplied.value
}
function onFeatureTagDropdownHide () {
  featureTagDraft.value = featureTagApplied.value
}

function resetActivityTagFilter () {
  activityTagDraft.value = null
  applyActivityTagFilter()
}

function applyActivityTagFilter () {
  activityTagApplied.value = activityTagDraft.value
  searchTrails(true)
  nextTick(() => {
    try {
      if (activityTagDropdownToggle?.value) {
        Dropdown.getOrCreateInstance(activityTagDropdownToggle.value).hide()
      }
    } catch (e) { /* noop */ }
  })
}

function onActivityTagDropdownShow () {
  activityTagDraft.value = activityTagApplied.value
}
function onActivityTagDropdownHide () {
  activityTagDraft.value = activityTagApplied.value
}

/* ---------- Searching ---------- */
/* ---------- Processing Status Polling ---------- */
function checkProcessingHikes() {
  const currentProcessingIds = new Set()

  trails.value.forEach(trail => {
    const status = trail.processingStatus
    if (status === 'pending' || status === 'processing') {
      currentProcessingIds.add(trail.id)
    }
  })

  processingHikeIds.value = currentProcessingIds

  // Start or stop polling based on whether we have processing hikes
  if (currentProcessingIds.size > 0 && !statusPollingInterval) {
    startStatusPolling()
  } else if (currentProcessingIds.size === 0 && statusPollingInterval) {
    stopStatusPolling()
  }
}

function startStatusPolling() {
  // Poll every 5 seconds
  statusPollingInterval = setInterval(() => {
    pollProcessingHikes()
  }, 5000)
}

function stopStatusPolling() {
  if (statusPollingInterval) {
    clearInterval(statusPollingInterval)
    statusPollingInterval = null
  }
}

async function pollProcessingHikes() {
  if (processingHikeIds.value.size === 0) return

  const variables = {
    query: (searchString.value || '').trim() || null,
    distanceMetersMin: (distanceRangeAppliedKm.value?.[0] || 0) > 0 ? Math.round(measurementPref.metresFromBigUnit(distanceRangeAppliedKm.value[0])) : null,
    distanceMetersMax: (distanceRangeAppliedKm.value?.[1] >= distanceMaxBound) ? null : Math.round(measurementPref.metresFromBigUnit(distanceRangeAppliedKm.value[1])),
    elevationGainMetersMin: (elevationRangeAppliedM.value?.[0] || 0) > 0 ? Math.round(measurementPref.metresFromSmallUnit(elevationRangeAppliedM.value[0])) : null,
    elevationGainMetersMax: (elevationRangeAppliedM.value?.[1] >= elevationMaxBound) ? null : Math.round(measurementPref.metresFromSmallUnit(elevationRangeAppliedM.value[1])),
    difficulties: (difficultyApplied.value?.length || 0) > 0 ? difficultyApplied.value : undefined,
    routeType: routeTypeApplied.value || undefined,
    featureTags: featureTagApplied.value ? [featureTagApplied.value] : undefined,
    customTags: customTagApplied.value ? [customTagApplied.value] : undefined,
    activityTags: activityTagApplied.value ? [activityTagApplied.value] : undefined,
    startDate: startDateApplied.value || null,
    endDate: endDateApplied.value || null,
    sort: sortApplied.value,
    page: 1,
    perPage: 100 // Get more to ensure we get all processing hikes
  }

  if (isPublicContext) {
    variables.userFirebaseId = publicFirebaseId
  }
  // Hide-completed applies in both contexts: own list hides the user's
  // completed hikes; public list hides the profile owner's completed hikes.
  variables.hideCompletedHikes = hideCompletedHikesApplied.value

  try {
    const requestPromise = isPublicContext
      ? graph_client.request(SearchPublicHikes, variables)
      : graph_client.request(SearchHikes, variables)

    const response = await requestPromise
    const fetchedTrails = isPublicContext ? response.searchPublicHikes : response.searchUserHikes

    // Check for hikes that finished processing
    fetchedTrails.forEach(fetchedTrail => {
      if (processingHikeIds.value.has(fetchedTrail.id)) {
        const status = fetchedTrail.processingStatus
        if (status === 'completed' || (!status)) {
          // Hike finished processing!
          showProcessingCompleteToast(fetchedTrail.name)

          // Update the trail in our current list
          const index = trails.value.findIndex(t => t.id === fetchedTrail.id)
          if (index !== -1) {
            trails.value[index] = fetchedTrail
          }
        }
      }
    })

    // Re-check which hikes are still processing
    checkProcessingHikes()
  } catch (err) {
    console.error('Error polling for processing status:', err)
  }
}

function showToast (title, bodyHtml) {
  const toastHtml = `
    <div class="toast-container position-fixed bottom-0 end-0 p-3">
      <div class="toast show" role="alert" aria-live="assertive" aria-atomic="true">
        <div class="toast-header bg-gray text-white">
          <strong class="me-auto">${title}</strong>
          <button type="button" class="btn-close btn-close-white" data-bs-dismiss="toast" aria-label="Close"></button>
        </div>
        <div class="toast-body bg-dark text-white">
          ${bodyHtml}
        </div>
      </div>
    </div>
  `

  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = toastHtml
  const toastElement = tempDiv.firstElementChild
  document.body.appendChild(toastElement)

  // Auto-remove after 5 seconds
  setTimeout(() => {
    toastElement.remove()
  }, 5000)
}

function showProcessingCompleteToast(hikeName) {
  showToast('Hike Ready', `<strong>${hikeName}</strong> has finished processing and is ready to view!`)
}

function searchTrailsClicked (e, reset = false) {
  e.preventDefault()
  searchTrails(reset)
}

function searchTrails (reset = false) {
  loading.value = true
  if (reset) { currentPage.value = 1; trails.value = [] }

  const variables = {
    query: (searchString.value || '').trim() || null,
    distanceMetersMin: (distanceRangeAppliedKm.value?.[0] || 0) > 0 ? Math.round(measurementPref.metresFromBigUnit(distanceRangeAppliedKm.value[0])) : null,
    distanceMetersMax: (distanceRangeAppliedKm.value?.[1] >= distanceMaxBound) ? null : Math.round(measurementPref.metresFromBigUnit(distanceRangeAppliedKm.value[1])),
    elevationGainMetersMin: (elevationRangeAppliedM.value?.[0] || 0) > 0 ? Math.round(measurementPref.metresFromSmallUnit(elevationRangeAppliedM.value[0])) : null,
    elevationGainMetersMax: (elevationRangeAppliedM.value?.[1] >= elevationMaxBound) ? null : Math.round(measurementPref.metresFromSmallUnit(elevationRangeAppliedM.value[1])),
    difficulties: (difficultyApplied.value?.length || 0) > 0 ? difficultyApplied.value : undefined,
    routeType: routeTypeApplied.value || undefined,
    featureTags: featureTagApplied.value ? [featureTagApplied.value] : undefined,
    customTags: customTagApplied.value ? [customTagApplied.value] : undefined,
    activityTags: activityTagApplied.value ? [activityTagApplied.value] : undefined,
    startDate: startDateApplied.value || null,
    endDate: endDateApplied.value || null,
    sort: sortApplied.value,
    page: currentPage.value,
    perPage: pageSize.value
  }

  if (isPublicContext) {
    variables.userFirebaseId = publicFirebaseId
  } else {
    // Only the user's own hikes search supports hiding completed hikes.
    variables.hideCompletedHikes = hideCompletedHikesApplied.value
  }

  const requestPromise = isPublicContext
    ? graph_client.request(SearchPublicHikes, variables)
    : graph_client.request(SearchHikes, variables)

  requestPromise
    .then((response) => {
      const newTrails = isPublicContext ? response.searchPublicHikes : response.searchUserHikes
      trails.value        = reset ? newTrails : trails.value.concat(newTrails)
      hasMoreTrails.value = newTrails.length === pageSize.value
      if (hasMoreTrails.value) currentPage.value += 1
      nextTick(initializeDropdowns)
      // Check if any hikes are processing and start polling if needed
      checkProcessingHikes()
    })
    .catch(err => console.error('Error fetching hikes:', err))
    .finally(() => { loading.value = false })
}

function resetHideCompletedFilter () {
  hideCompletedHikesDraft.value = true
  applyHideCompletedFilter()
}

function applyHideCompletedFilter () {
  hideCompletedHikesApplied.value = hideCompletedHikesDraft.value
  searchTrails(true)
  nextTick(() => {
    try {
      if (hideCompletedDropdownToggle?.value) {
        Dropdown.getOrCreateInstance(hideCompletedDropdownToggle.value).hide()
      }
    } catch (e) { /* noop */ }
  })
}

function onHideCompletedDropdownShow () {
  hideCompletedHikesDraft.value = hideCompletedHikesApplied.value
}
function onHideCompletedDropdownHide () {
  hideCompletedHikesDraft.value = hideCompletedHikesApplied.value
}

function clearSearchFilters (e) {
  e.preventDefault()
  searchString.value = ''
  startDateApplied.value = null
  endDateApplied.value   = null
  startDateDraft.value   = null
  endDateDraft.value     = null
  distanceRangeAppliedKm.value = [0, distanceMaxBound]
  distanceRangeDraftKm.value   = [0, distanceMaxBound]
  elevationRangeAppliedM.value = [0, elevationMaxBound]
  elevationRangeDraftM.value   = [0, elevationMaxBound]
  difficultyApplied.value = []
  difficultyDraft.value   = []
  routeTypeApplied.value = null
  routeTypeDraft.value   = null
  featureTagApplied.value = null
  featureTagDraft.value = null
  activityTagApplied.value = null
  activityTagDraft.value = null
  customTagApplied.value = null
  customTagDraft.value = null
  sortApplied.value = 'created_desc'
  sortDraft.value   = 'created_desc'
  hideCompletedHikesApplied.value = true
  hideCompletedHikesDraft.value   = true
  searchTrails(true)
}

function clearAllFilters () {
  searchString.value = ''
  startDateApplied.value = null
  endDateApplied.value   = null
  startDateDraft.value   = null
  endDateDraft.value     = null
  distanceRangeAppliedKm.value = [0, distanceMaxBound]
  distanceRangeDraftKm.value   = [0, distanceMaxBound]
  elevationRangeAppliedM.value = [0, elevationMaxBound]
  elevationRangeDraftM.value   = [0, elevationMaxBound]
  difficultyApplied.value = []
  difficultyDraft.value   = []
  routeTypeApplied.value = null
  routeTypeDraft.value   = null
  featureTagApplied.value = null
  featureTagDraft.value = null
  activityTagApplied.value = null
  activityTagDraft.value = null
  customTagApplied.value = null
  customTagDraft.value = null
  sortApplied.value = 'created_desc'
  sortDraft.value   = 'created_desc'
  hideCompletedHikesApplied.value = true
  hideCompletedHikesDraft.value   = true
  searchTrails(true)
}

/* ---------- Actions ---------- */
function getDifficultyClass (trail) {
  const difficulty = (trail.difficulty || 'medium').toLowerCase()
  if (difficulty === 'easy') return 'difficulty-easy'
  if (difficulty === 'medium') return 'difficulty-medium'
  if (difficulty === 'difficult' || difficulty === 'hard') return 'difficulty-hard'
  if (difficulty === 'extra difficult') return 'difficulty-extra'
  return 'difficulty-medium'
}

function printTrail (trail) {
  return `/print-maps?data_ids[]=${trail.id}&data_types[]=Hike`
}

function showFlyover (e, trail) {
  if (e && e.preventDefault) e.preventDefault()
  if (window.showMapModalForTrail) {
    window.showMapModalForTrail(trail.id)
  } else {
    console.error('Flyover modal launcher not found (showMapModalForTrail)')
  }
}

async function exportGpx (trail) {
  try {
    const response = await graph_client.request(GetHike, { id: trail.id })

    const hike = response?.hike
    if (!hike) {
      throw new Error('Hike not found')
    }

    const context = buildHikeGpxContext(hike)
    const {
      lineFeatures = [],
      metadata = { creator: 'Hiiker', metadata: {} },
      fileName
    } = context || {}

    if (!lineFeatures.length) {
      window.alert('No GPX data is available for this hike yet.')
      return
    }

    const featureCollection = {
      type: 'FeatureCollection',
      features: lineFeatures
    }

    const gpx = geojsonToGpx(featureCollection, null, {
      ...metadata,
      outputType: 'rte',
      routeName: metadata.metadata?.name || 'Hike',
      mergeRoutesByProperty: 'hikeDayNumber'
    })
    downloadGpxFile(gpx, fileName)
  } catch (err) {
    console.error('Error exporting GPX:', err)
    alert('Something went wrong generating the GPX. Please try again.')
  }
}

function buildHikeGpxContext (hike) {
  const metadata = {
    creator: 'Hiiker',
    metadata: {}
  }

  const title = hike?.name || hike?.title || hike?.slug
  if (title) metadata.metadata.name = title
  if (hike?.createdAt) metadata.metadata.time = hike.createdAt

  const geojson = parseHikeGeojson(hike?.geojsonMultilinestringsWithStartEndPointsString)
  const lineFeatures = collectLineFeaturesFromGeojson(geojson)

  return {
    lineFeatures,
    metadata,
    fileName: composeGpxFileName(title || 'hike')
  }
}

function parseHikeGeojson (geojsonLike) {
  if (!geojsonLike) return null

  if (typeof geojsonLike === 'string') {
    try {
      return JSON.parse(geojsonLike)
    } catch (error) {
      console.error('Failed to parse hike geojson string', error)
      return null
    }
  }

  if (geojsonLike?.type === 'FeatureCollection') {
    return geojsonLike
  }

  return null
}

function collectLineFeaturesFromGeojson (geojson) {
  if (!geojson || !Array.isArray(geojson.features)) {
    return []
  }

  const results = []

  geojson.features.forEach(feature => {
    const geometry = feature?.geometry
    if (!geometry) return

    if (geometry.type === 'LineString') {
      const coords = geometry.coordinates
      if (Array.isArray(coords) && coords.length > 1) {
        results.push({
          type: 'Feature',
          properties: feature.properties ? { ...feature.properties } : {},
          geometry: {
            type: 'LineString',
            coordinates: coords
          }
        })
      }
      return
    }

    if (geometry.type === 'MultiLineString') {
      const lines = geometry.coordinates || []
      lines.forEach(line => {
        if (Array.isArray(line) && line.length > 1) {
          results.push({
            type: 'Feature',
            properties: feature.properties ? { ...feature.properties } : {},
            geometry: {
              type: 'LineString',
              coordinates: line
            }
          })
        }
      })
    }
  })

  return results
}

function composeGpxFileName (label) {
  const base = (label || 'hiiker').toString()
  const slug = base
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/gi, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase() || 'hiiker'

  return `${slug}.gpx`
}

function downloadGpxFile (gpxContent, fileName) {
  const blob = new Blob([gpxContent], { type: 'application/gpx+xml' })
  const url = URL.createObjectURL(blob)

  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = fileName || 'hiiker.gpx'
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)

  URL.revokeObjectURL(url)
}

/* ---------- Edit Details (lightweight modal) ---------- */
const editDetailsModal = ref(null)
const editDetailsHike = ref(null)

function openEditDetails (trail) {
  if (isPublicContext) return
  editDetailsHike.value = trail
  nextTick(() => editDetailsModal.value?.show())
}

async function saveHikeDetails (formData, { done, fail }) {
  const hike = editDetailsHike.value
  if (!hike?.id) return fail('No hike selected')

  try {
    const result = await graph_client.request(UpdateHikeDetails, {
      input: {
        id: hike.id,
        name: formData.name,
        description: formData.description,
        difficulty: formData.difficulty,
        publiclyVisible: formData.publiclyVisible
      }
    })

    const errors = result?.updateHikeDetails?.errors
    if (errors && errors.length > 0) {
      return fail(errors.map(e => e.errors.join(', ')).join('; '))
    }

    // Update the trail in our local list so the UI refreshes immediately
    const updated = result?.updateHikeDetails?.hike
    if (updated) {
      const idx = trails.value.findIndex(t => t.id === hike.id)
      if (idx !== -1) {
        trails.value[idx] = { ...trails.value[idx], ...updated }
      }
    }

    done()
  } catch (err) {
    console.error('UpdateHikeDetails failed:', err)
    fail(extractGraphQLError(err) || err.message || 'Failed to update hike details')
  }
}

function editHike (trail) {
  if (isPublicContext) return
  window.location.href = `${trail.shareableUrl}/edit`
}

async function destroyHike (trail) {
  if (isPublicContext) return
  if (!trail?.id) return
  if (!window.confirm('Are you sure you want to delete this hike? This action cannot be undone.')) {
    return
  }

  let primaryError = null

  try {
    const deletedId = await attemptDestroyHikeById(trail.id)
    handleDestroySuccess(deletedId)
    return
  } catch (err) {
    primaryError = err
    alert(`Failed to delete hike: ${extractGraphQLError(err) || err.message || err.toString()}`)
    console.error('DestroyHike GraphQL mutation failed, attempting fallback:', err)
  }
}

async function duplicateHike (trail) {
  if (isPublicContext) return
  if (!trail?.id) return

  try {
    const data = await graph_client.request(DuplicateHike, { input: { id: trail.id } })
    const newHike = data.duplicateHike
    if (newHike?.id) {
      trails.value = [newHike, ...trails.value]
      showToast('Hike Duplicated', `<strong>${newHike.name}</strong> has been created successfully.`)
    } else {
      alert('Failed to duplicate hike.')
    }
  } catch (err) {
    alert(`Failed to duplicate hike: ${extractGraphQLError(err) || err.message || err.toString()}`)
    console.error('DuplicateHike mutation failed:', err)
  }
}

async function removeOfficialStatus (trail) {
  if (isPublicContext) return
  if (!trail?.id) return
  if (!window.confirm('Are you sure you want to remove official status from this hike? It will be removed from public search results.')) {
    return
  }

  try {
    const data = await graph_client.request(UpdateHikeWithPlannedTrails, {
      input: { id: trail.id, removeOfficial: true }
    })
    const updatedHike = data.updateHike?.hike
    if (updatedHike) {
      const index = trails.value.findIndex(t => t.id === trail.id)
      if (index !== -1) {
        trails.value[index] = { ...trails.value[index], madeOfficialAt: null, slug: null }
      }
      showToast('Official Status Removed', `<strong>${trail.name}</strong> is no longer official.`)
    } else {
      const errors = data.updateHike?.errors
      const msg = errors?.map(e => e.errors.join(', ')).join('; ') || 'Unknown error'
      alert(`Failed to remove official status: ${msg}`)
    }
  } catch (err) {
    alert(`Failed to remove official status: ${extractGraphQLError(err) || err.message || err.toString()}`)
    console.error('RemoveOfficial mutation failed:', err)
  }
}

async function submitHikeForOfficial (trail) {
  if (isPublicContext) return
  if (!trail?.id) return
  if (!window.confirm('Submit this hike to be reviewed and made official? Our team will review it before it appears in public search results.')) {
    return
  }

  try {
    const data = await graph_client.request(SubmitHikeForOfficial, { input: { id: trail.id } })
    const result = data.submitHikeForOfficial
    const errors = result?.errors
    if (errors?.length) {
      alert(`Failed to submit hike: ${errors.map(e => e.errors.join(', ')).join('; ')}`)
      return
    }

    const requestedAt = result?.hike?.officialHikeApprovalRequestedAt
    const index = trails.value.findIndex(t => t.id === trail.id)
    if (index !== -1) {
      trails.value[index] = { ...trails.value[index], officialHikeApprovalRequestedAt: requestedAt }
    }
    showToast('Submitted for Review', `<strong>${trail.name}</strong> has been submitted to be made official.`)
  } catch (err) {
    alert(`Failed to submit hike: ${extractGraphQLError(err) || err.message || err.toString()}`)
    console.error('SubmitHikeForOfficial mutation failed:', err)
  }
}

function handleDestroySuccess (deletedId) {
  trails.value = trails.value.filter(t => t.id !== deletedId)

  if (selectedHike.value?.id === deletedId) {
    clearSelectedHike()
  }

  if (hasMoreTrails.value) {
    searchTrails(false)
  }
}

async function attemptDestroyHikeById (id) {
  const { destroyHike: result } = await graph_client.request(DestroyHike, { input: { id } })
  if (result?.id) return result.id
  throw new Error('DestroyHike mutation returned no id')
}

async function attemptDestroyUserHikesById (id) {
  const { destroyUserHikes: result } = await graph_client.request(DestroyUserHikes, { input: { ids: [id] } })
  if (result?.destroyed && result.destroyedCount > 0) return id

  const payloadErrors = formatPayloadErrors(result?.errors)
  if (payloadErrors) throw new Error(payloadErrors)

  throw new Error('DestroyUserHikes mutation returned no changes')
}

/* ---------- Bulk Delete / Selection Mode Functions ---------- */
function toggleSelectionMode () {
  selectionMode.value = !selectionMode.value
  if (!selectionMode.value) {
    // Clear selection when exiting selection mode
    selectedHikeIds.value.clear()
  }
}

function toggleHikeSelection (hikeId) {
  if (selectedHikeIds.value.has(hikeId)) {
    selectedHikeIds.value.delete(hikeId)
  } else {
    selectedHikeIds.value.add(hikeId)
  }
  // Trigger reactivity for Set
  selectedHikeIds.value = new Set(selectedHikeIds.value)
}

function selectAllHikes () {
  trails.value.forEach(trail => {
    if (trail.id) selectedHikeIds.value.add(trail.id)
  })
  selectedHikeIds.value = new Set(selectedHikeIds.value)
}

function deselectAllHikes () {
  selectedHikeIds.value.clear()
  selectedHikeIds.value = new Set(selectedHikeIds.value)
}

async function bulkDeleteHikes () {
  if (isPublicContext) return
  if (selectedHikeIds.value.size === 0) return

  const count = selectedHikeIds.value.size
  const message = `Are you sure you want to delete ${count} hike${count !== 1 ? 's' : ''}? This action cannot be undone.`

  if (!window.confirm(message)) {
    return
  }

  bulkDeleteInProgress.value = true
  const idsToDelete = Array.from(selectedHikeIds.value)
  const deletedIds = []
  const failedIds = []

  try {
    // Delete in batches of 10 to avoid overwhelming the server
    const batchSize = 10
    for (let i = 0; i < idsToDelete.length; i += batchSize) {
      const batch = idsToDelete.slice(i, i + batchSize)

      try {
        const { destroyUserHikes: result } = await graph_client.request(DestroyUserHikes, {
          input: { ids: batch }
        })

        if (result?.destroyed && result.destroyedCount > 0) {
          deletedIds.push(...batch)
        } else {
          failedIds.push(...batch)
        }
      } catch (err) {
        console.error('Batch delete failed:', err)
        failedIds.push(...batch)
      }
    }

    // Remove successfully deleted hikes from the UI
    if (deletedIds.length > 0) {
      trails.value = trails.value.filter(t => !deletedIds.includes(t.id))

      // Clear selected hike if it was deleted
      if (selectedHike.value && deletedIds.includes(selectedHike.value.id)) {
        clearSelectedHike()
      }
    }

    // Show results
    if (failedIds.length === 0) {
      alert(`Successfully deleted ${deletedIds.length} hike${deletedIds.length !== 1 ? 's' : ''}.`)
    } else if (deletedIds.length === 0) {
      alert(`Failed to delete all ${count} hike${count !== 1 ? 's' : ''}.`)
    } else {
      alert(`Deleted ${deletedIds.length} hike${deletedIds.length !== 1 ? 's' : ''}. Failed to delete ${failedIds.length}.`)
    }

    // Exit selection mode and refresh if needed
    selectionMode.value = false
    selectedHikeIds.value.clear()

    if (hasMoreTrails.value && deletedIds.length > 0) {
      await searchTrails(false)
    }
  } catch (err) {
    console.error('Bulk delete error:', err)
    alert(`An error occurred during bulk delete: ${err.message || err.toString()}`)
  } finally {
    bulkDeleteInProgress.value = false
  }
}

function extractGraphQLError (error) {
  if (!error) return ''

  const responseErrors = error?.response?.errors
  if (Array.isArray(responseErrors) && responseErrors.length > 0) {
    return responseErrors.map(e => e?.message).filter(Boolean).join('; ')
  }

  return error?.message || ''
}

function formatPayloadErrors (errors) {
  if (!Array.isArray(errors) || errors.length === 0) return ''
  return errors
    .map(err => Array.isArray(err?.errors) ? err.errors.join(', ') : null)
    .filter(Boolean)
    .join('; ')
}

function triggerCopyModel (e, trail) {
  e.preventDefault()
  const ownerFirebaseId = publicFirebaseId || window.user?.firebaseUserId || window.user?.firebase_user_id
  const pageLink = ownerFirebaseId ? `${window.location.origin}/users/${ownerFirebaseId}/hikes` : window.location.href
  const link = trail?.shareableUrl || trail?.shareable_url || trail?.canonicalUrl || pageLink
  nextTick(() => {
    if (shareModal.value && typeof shareModal.value.show === 'function') {
      shareModal.value.show(link, trail, 'hike')
    }
  })
}

function shareCurrentPage () {
  const ownerFirebaseId = publicFirebaseId || window.user?.firebaseUserId || window.user?.firebase_user_id
  const link = ownerFirebaseId ? `${window.location.origin}/users/${ownerFirebaseId}/hikes` : window.location.href
  nextTick(() => {
    if (shareModal.value && typeof shareModal.value.show === 'function') {
      shareModal.value.show(link)
    }
  })
}

async function openImportDialog () {
  try {
    // Ensure the import dialog is mounted
    if (!document.getElementById('import-hike-dialog-backdrop')) {
      // Check if the mount point exists
      if (!document.getElementById('vue-import-hike-dialog')) {
        const el = document.createElement('div')
        el.id = 'vue-import-hike-dialog'
        document.body.appendChild(el)
      }

      // Dynamically import and mount the component
      await import('../import_hike/main')

      // Wait a bit for Vue to mount
      await nextTick()
    }

    // Now open the modal
    const element = document.getElementById('import-hike-dialog-backdrop')
    if (element) {
      const modal = Modal.getOrCreateInstance(element)
      modal.show()
    } else {
      console.error('Import hike dialog modal element not found')
    }
  } catch (e) {
    console.error('Failed to open import hike dialog:', e)
  }
}

function launchAddToList (trail) {
  if (!trail) return

  const onboarding = window.onBoardingVue
  if (!onboarding || typeof onboarding.launchFor !== 'function') {
    console.warn('Onboarding flow is not available')
    return
  }

  const rawHikeId = trail.id ?? trail.hikeId
  if (rawHikeId == null) {
    console.warn('Cannot open add-to-list flow without a hike id')
    return
  }

  const context = {
    hikeId: rawHikeId.toString(),
    trailId: Number(rawHikeId),
    slug: trail.slug,
    regionSlug: trail.mainRegion?.slug,
    countrySlug: trail.mainCountry?.slug
  }

  try {
    onboarding.setAddToListContext?.(context)
  } catch (error) {
    console.warn('Unable to set onboarding context for hike list', error)
  }

  onboarding.launchFor(OnBoardingGoal.AddTrailToList)
}

function getOverviewUrl (trail) {
  return trail.shareableUrl
}

function getMapUrl (trail) {
  return `${trail.shareableUrl}/map`
}

/* ---------- Duration Formatting ---------- */
function formatDuration (trail) {
  if (trail?.trailType === 'multi_day') {
    const days = Array.isArray(trail?.hikeDays) ? trail.hikeDays.length : 0
    if (days <= 0) return '—'
    return days === 1 ? '1 day' : `${days} days`
  }
  const distance = trail?.distanceMeters
  const elevGain = trail?.elevationGainMeters
  if ((distance || distance === 0) && (elevGain || elevGain === 0)) {
    const secs = HikeTimeUtility.preference().secondsDuration(elevGain, distance)
    return new PrettyTime(secs).prettyTime()
  }
  if (trail?.durationDisplay) return trail.durationDisplay
  return ''
}

function resetDistanceFilter () {
  distanceRangeDraftKm.value = [0, distanceMaxBound]
  applyDistanceFilter()
}

function applyDistanceFilter () {
  // Commit draft to applied, then search and close dropdown
  distanceRangeAppliedKm.value = [...distanceRangeDraftKm.value]
  searchTrails(true)
  nextTick(() => {
    try {
      if (distanceDropdownToggle?.value) {
        Dropdown.getOrCreateInstance(distanceDropdownToggle.value).hide()
      }
    } catch (e) { /* noop */ }
  })
}

// Keep draft in sync with applied when opening/closing without applying
function onDistanceDropdownShow () {
  distanceRangeDraftKm.value = [...distanceRangeAppliedKm.value]
}
function onDistanceDropdownHide () {
  distanceRangeDraftKm.value = [...distanceRangeAppliedKm.value]
}

function resetElevationFilter () {
  elevationRangeDraftM.value = [0, elevationMaxBound]
  applyElevationFilter()
}

function applyElevationFilter () {
  elevationRangeAppliedM.value = [...elevationRangeDraftM.value]
  searchTrails(true)
  nextTick(() => {
    try {
      if (elevationDropdownToggle?.value) {
        Dropdown.getOrCreateInstance(elevationDropdownToggle.value).hide()
      }
    } catch (e) { /* noop */ }
  })
}

function onElevationDropdownShow () {
  elevationRangeDraftM.value = [...elevationRangeAppliedM.value]
}
function onElevationDropdownHide () {
  elevationRangeDraftM.value = [...elevationRangeAppliedM.value]
}

function resetDifficultyFilter () {
  difficultyDraft.value = []
  applyDifficultyFilter()
}

function applyDifficultyFilter () {
  difficultyApplied.value = [...difficultyDraft.value]
  searchTrails(true)
  nextTick(() => {
    try {
      if (difficultyDropdownToggle?.value) {
        Dropdown.getOrCreateInstance(difficultyDropdownToggle.value).hide()
      }
    } catch (e) { /* noop */ }
  })
}

function onDifficultyDropdownShow () {
  difficultyDraft.value = [...difficultyApplied.value]
}
function onDifficultyDropdownHide () {
  difficultyDraft.value = [...difficultyApplied.value]
}

function resetRouteTypeFilter () {
  routeTypeDraft.value = null
  applyRouteTypeFilter()
}

function applyRouteTypeFilter () {
  routeTypeApplied.value = routeTypeDraft.value
  searchTrails(true)
  nextTick(() => {
    try {
      if (routeTypeDropdownToggle?.value) {
        Dropdown.getOrCreateInstance(routeTypeDropdownToggle.value).hide()
      }
    } catch (e) { /* noop */ }
  })
}

function onRouteTypeDropdownShow () {
  routeTypeDraft.value = routeTypeApplied.value
}
function onRouteTypeDropdownHide () {
  routeTypeDraft.value = routeTypeApplied.value
}

function resetDateFilter () {
  startDateDraft.value = null
  endDateDraft.value   = null
  applyDateFilter()
}

function applyDateFilter () {
  startDateApplied.value = startDateDraft.value
  endDateApplied.value   = endDateDraft.value
  searchTrails(true)
  nextTick(() => {
    try {
      if (dateDropdownToggle?.value) {
        Dropdown.getOrCreateInstance(dateDropdownToggle.value).hide()
      }
    } catch (e) { /* noop */ }
  })
}

function onDateDropdownShow () {
  startDateDraft.value = startDateApplied.value
  endDateDraft.value   = endDateApplied.value
}
function onDateDropdownHide () {
  startDateDraft.value = startDateApplied.value
  endDateDraft.value   = endDateApplied.value
}

function resetSortFilter () {
  sortDraft.value = 'created_desc'
  applySortFilter()
}

function applySortFilter () {
  sortApplied.value = sortDraft.value
  searchTrails(true)
  nextTick(() => {
    try {
      if (sortDropdownToggle?.value) {
        Dropdown.getOrCreateInstance(sortDropdownToggle.value).hide()
      }
    } catch (e) { /* noop */ }
  })
}

function onSortDropdownShow () {
  sortDraft.value = sortApplied.value
}
function onSortDropdownHide () {
  sortDraft.value = sortApplied.value
}

onMounted(() => {
  if (distanceDropdownToggle?.value) {
    const el = distanceDropdownToggle.value
    el.addEventListener('show.bs.dropdown', onDistanceDropdownShow)
    el.addEventListener('hide.bs.dropdown', onDistanceDropdownHide)
  }
  if (elevationDropdownToggle?.value) {
    const el2 = elevationDropdownToggle.value
    el2.addEventListener('show.bs.dropdown', onElevationDropdownShow)
    el2.addEventListener('hide.bs.dropdown', onElevationDropdownHide)
  }
  if (difficultyDropdownToggle?.value) {
    const el3 = difficultyDropdownToggle.value
    el3.addEventListener('show.bs.dropdown', onDifficultyDropdownShow)
    el3.addEventListener('hide.bs.dropdown', onDifficultyDropdownHide)
  }
  if (routeTypeDropdownToggle?.value) {
    const elRt = routeTypeDropdownToggle.value
    elRt.addEventListener('show.bs.dropdown', onRouteTypeDropdownShow)
    elRt.addEventListener('hide.bs.dropdown', onRouteTypeDropdownHide)
  }
  if (dateDropdownToggle?.value) {
    const el4 = dateDropdownToggle.value
    el4.addEventListener('show.bs.dropdown', onDateDropdownShow)
    el4.addEventListener('hide.bs.dropdown', onDateDropdownHide)
  }
  if (sortDropdownToggle?.value) {
    const el5 = sortDropdownToggle.value
    el5.addEventListener('show.bs.dropdown', onSortDropdownShow)
    el5.addEventListener('hide.bs.dropdown', onSortDropdownHide)
  }
  if (featureTagDropdownToggle?.value) {
    const el6 = featureTagDropdownToggle.value
    el6.addEventListener('show.bs.dropdown', onFeatureTagDropdownShow)
    el6.addEventListener('hide.bs.dropdown', onFeatureTagDropdownHide)
  }
  if (activityTagDropdownToggle?.value) {
    const el7 = activityTagDropdownToggle.value
    el7.addEventListener('show.bs.dropdown', onActivityTagDropdownShow)
    el7.addEventListener('hide.bs.dropdown', onActivityTagDropdownHide)
  }
  if (hideCompletedDropdownToggle?.value) {
    const el8 = hideCompletedDropdownToggle.value
    el8.addEventListener('show.bs.dropdown', onHideCompletedDropdownShow)
    el8.addEventListener('hide.bs.dropdown', onHideCompletedDropdownHide)
  }
})

onBeforeUnmount(() => {
  if (distanceDropdownToggle?.value) {
    const el = distanceDropdownToggle.value
    el.removeEventListener('show.bs.dropdown', onDistanceDropdownShow)
    el.removeEventListener('hide.bs.dropdown', onDistanceDropdownHide)
  }
  if (elevationDropdownToggle?.value) {
    const el2 = elevationDropdownToggle.value
    el2.removeEventListener('show.bs.dropdown', onElevationDropdownShow)
    el2.removeEventListener('hide.bs.dropdown', onElevationDropdownHide)
  }
  if (difficultyDropdownToggle?.value) {
    const el3 = difficultyDropdownToggle.value
    el3.removeEventListener('show.bs.dropdown', onDifficultyDropdownShow)
    el3.removeEventListener('hide.bs.dropdown', onDifficultyDropdownHide)
  }
  if (routeTypeDropdownToggle?.value) {
    const elRt = routeTypeDropdownToggle.value
    elRt.removeEventListener('show.bs.dropdown', onRouteTypeDropdownShow)
    elRt.removeEventListener('hide.bs.dropdown', onRouteTypeDropdownHide)
  }
  if (dateDropdownToggle?.value) {
    const el4 = dateDropdownToggle.value
    el4.removeEventListener('show.bs.dropdown', onDateDropdownShow)
    el4.removeEventListener('hide.bs.dropdown', onDateDropdownHide)
  }
  if (sortDropdownToggle?.value) {
    const el5 = sortDropdownToggle.value
    el5.removeEventListener('show.bs.dropdown', onSortDropdownShow)
    el5.removeEventListener('hide.bs.dropdown', onSortDropdownHide)
  }
  if (featureTagDropdownToggle?.value) {
    const el6 = featureTagDropdownToggle.value
    el6.removeEventListener('show.bs.dropdown', onFeatureTagDropdownShow)
    el6.removeEventListener('hide.bs.dropdown', onFeatureTagDropdownHide)
  }
  if (activityTagDropdownToggle?.value) {
    const el7 = activityTagDropdownToggle.value
    el7.removeEventListener('show.bs.dropdown', onActivityTagDropdownShow)
    el7.removeEventListener('hide.bs.dropdown', onActivityTagDropdownHide)
  }
  if (hideCompletedDropdownToggle?.value) {
    const el8 = hideCompletedDropdownToggle.value
    el8.removeEventListener('show.bs.dropdown', onHideCompletedDropdownShow)
    el8.removeEventListener('hide.bs.dropdown', onHideCompletedDropdownHide)
  }
})
</script>

<style scoped>
.hikes-app {
  color: #fff;
}

.btn-dark-outline {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: rgba(255, 255, 255, 0.8);
  padding: 0.6rem 1.2rem;
  border-radius: 8px;
  display: flex;
  align-items: center;
  transition: all 0.2s;
  font-size: 14px;
  font-weight: 500;
}

.btn-dark-outline:hover {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.25);
  color: #fff;
}

.btn-dark-outline i {
  font-size: 20px;
  margin-right: 0.5rem;
}


/* Make filters horizontally scrollable to avoid layout push on wide screens */
.filters-row {
  flex-wrap: nowrap;
  overflow-x: auto;
  overflow-y: visible; /* allow dropdown menus to escape vertically */
  -webkit-overflow-scrolling: touch;
  position: relative;
  z-index: 2; /* keep menus above nearby elements */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE/Edge */
}
.filters-row::-webkit-scrollbar {
  display: none; /* Chrome/Safari/WebKit */
}
.filters-row > .dropdown,
.filters-row > .btn,
.filters-row > button {
  flex: 0 0 auto;
}

/* Active filter state */
.btn-filter.active {
  background: #2b2c34;
  border-color: #FBCD01;
  color: #fff;
  position: relative;
}

.filter-indicator {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 6px;
  height: 6px;
  background: #FBCD01;
  border-radius: 50%;
}

/* Clear All Filters Button */
.btn-clear-all {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.7);
  padding: 0.5rem 1rem;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 14px;
  font-weight: 500;
  min-width: auto;
}

.btn-clear-all:hover {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.3);
  color: #fff;
}

/* Search */
.search-input-wrapper {
  position: relative;
}

.search-icon {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.4);
  font-size: 20px;
  pointer-events: none;
}

.search-input {
  background: #1f2026;
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: #fff;
  padding: 0.75rem 1rem 0.75rem 3rem;
  border-radius: 118px;
  font-size: 14px;
  height: 44px;
}

.search-input:focus {
  outline: none;
  border-color: rgba(255, 255, 255, 0.2);
  background: #252530;
}

.search-input::placeholder {
  color: rgba(255, 255, 255, 0.4);
}

/* removed location filter styles */

/* List */
.hikes-list {
  margin-top: 1.5rem;
}

.list-header {
  padding: 0 0 1rem;
  margin-bottom: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.list-title {
  color: rgba(255, 255, 255, 0.4);
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 600;
}

/* Hike Items */
.hikes-items {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.hike-item {
  background: #1f2026;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  transition: all 0.2s;
}

.hike-item:hover {
  background: #252530;
  border-color: rgba(255, 255, 255, 0.12);
}

.hike-content {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  height: 100px;
}

.hike-thumbnail-wrapper {
  position: relative;
  width: 160px;
  height: -webkit-fill-available;
  background: var(--gray);
  flex: 0 0 15%;
  overflow: hidden;
}

.hike-thumbnail {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.play-button {
  position: absolute;
  top: .5em;
  left: .5em;
  background: rgba(0, 0, 0, 0.6);
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  cursor: pointer;
  transition: all 0.2s;
  backdrop-filter: blur(4px);
}

.play-button:hover {
  background: rgba(0, 0, 0, 0.9);
}

.play-button i {
  font-size: 20px;
  margin-left: 2px;
}

/* Hike Info */
.hike-info {
  flex: 0 0 23%;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.difficulty-badge {
  display: inline-block;
  padding: 2px 5px;
  border-radius: 4px;
  font-size: 9px;
  font-weight: 600;
  text-transform: capitalize;
  letter-spacing: 0.5px;
  align-self: flex-start;
}

.difficulty-easy {
  background: #4BC263;
  color: var(--dark);
}

.difficulty-medium {
  background: #F57700;
  color: var(--dark);
}

.difficulty-hard {
  background: #DB2B35;
  color: #fff;
}

.difficulty-extra {
  background: #8B008B;
  color: #fff;
}

.hike-title {
  font-size: 20px;
  font-weight: 700;
  margin: 0;
  line-height: 1.2;
}

.hike-name-link {
  color: #fff;
  text-decoration: none;
  transition: text-decoration 0.2s;
}

.hike-name-link:hover {
  color: #fff;
  text-decoration: underline;
}

.hike-location {
  color: rgba(255, 255, 255, 0.5);
  font-size: 14px;
  margin: 0;
  line-height: 1.4;
}

.hike-stats {
  display: flex;
  flex: 0 0 32%;
  gap: 3rem;
  margin-top: 0.75rem;
}

.stat {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.stat-label {
  font-size: .5em;
  letter-spacing: 0.5px;
  color: #a6a6a6;
}

.stat-value {
  color: #fff;
  font-size: 15px;
  font-weight: 600;
}

/* Action Buttons */
.hike-actions {
  display: flex;
  flex: 1;
  align-items: center;
  padding-right: 1em;
  gap: 0.75rem;
  margin-left: auto;
}

.btn-icon {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: rgba(255, 255, 255, 0.7);
  width: 36px;
  height: 36px !important;
  border-radius:100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: all 0.2s;
  cursor: pointer;
}

.btn-icon:hover {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.25);
  color: #fff;
}

.btn-icon i {
  font-size: 20px;
}

.btn-yellow {
  background: #FBCD01;
  color: #000;
  border: none;
}

.btn-yellow:hover {
  background: #e6ba00;
}

.btn-yellow i {
  color: #000;
}

/* Dropdowns */
.dropdown-toggle::after {
  display: none;
}

.dropdown-menu {
  background: #252530;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  border-radius: 8px;
  padding: 0.5rem;
  margin-top: 0.5rem;
  z-index: 1100;
}

.dropdown-item {
  color: rgba(255, 255, 255, 0.9);
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 14px;
}

.dropdown-item:hover {
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
}

/* See More */
.see-more {
  margin-top: 2rem;
  padding-top: 1rem;
}

.see-more button {
  color: #FBCD01;
  text-decoration: none;
  font-size: 14px;
  font-weight: 600;
  background: none;
  border: none;
  cursor: pointer;
}

.see-more button:hover {
  text-decoration: underline;
}

/* No trails */
.no-trails {
  text-align: center;
  padding: 4rem 2rem;
  color: rgba(255, 255, 255, 0.4);
}

/* Map View */
.hikes-map-container {
  width: 100%;
  height: calc(100vh - 220px);
  min-height: 420px;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.08);
  position: relative;
}

/* Hike overlay on map */
.hike-overlay {
  position: absolute;
  top: 12px;
  right: 12px;
  max-width: 720px;
  z-index: 10;
}

.hike-overlay-card {
  position: relative;
}

.overlay-close {
  position: absolute;
  top: 6px;
  right: 6px;
  background: rgba(0,0,0,0.5);
  color: #fff;
  border: none;
  border-radius: 6px;
  width: 28px;
  height: 28px;
  cursor: pointer;
  z-index: 11;
}

.hike-overlay-loading {
  background: #1f2026;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 12px 16px;
}

/* Modal Overrides */
.modal-content {
  background: #252530;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
}

.modal-header {
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding: 1.5rem;
}

.modal-title {
  color: #fff;
  font-size: 20px;
}

.modal-body {
  padding: 1.5rem;
}

.close {
  color: rgba(255, 255, 255, 0.6);
  font-size: 1.5rem;
  background: none;
  border: none;
  cursor: pointer;
}

.close:hover {
  color: #fff;
}


.text-yellow {
  color: #FBCD01 !important;
}

.text-muted {
  color: rgba(255, 255, 255, 0.5) !important;
}

/* Hide vue-slider tooltips completely */
:deep(.vue-slider-dot-tooltip),
:deep(.vue-slider-tooltip) {
  display: none !important;
}

/* View Toggle */
.view-toggle {
  display: inline-flex;
  align-items: center;
  background: #1f2026;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 9999px;
  overflow: hidden;
  height: 35px;
}

.view-toggle-btn {
  appearance: none;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.8);
  width: 52px;
  height: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.view-toggle-btn + .view-toggle-btn {
  border-left: 1px solid rgba(255, 255, 255, 0.08);
}

.view-toggle-btn:hover {
  background: #252530;
}

.view-toggle-btn.active {
  background: #2b2c34;
  color: #fff;
}

/* ---------------- Responsive Tweaks ---------------- */
/* Medium screens: stack hike item content vertically */
@media (max-width: 992px) {
  .hike-content {
    flex-direction: column;
    align-items: stretch;
    height: auto;
    padding: 0.75rem;
    gap: 0.75rem;
  }

  .hike-thumbnail-wrapper {
    width: 100%;
    height: 180px; /* fixed height for consistent thumbs */
    flex: none;
    border-top-left-radius: 12px;
    border-top-right-radius: 12px;
  }

  .hike-info {
    flex: none;
  }

  .hike-stats {
    flex: none;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.75rem;
    margin-top: 0;
  }

  .hike-actions {
    flex: none;
    justify-content: flex-start;
    padding: 0 .25rem .5rem;
    margin-left: 0;
  }
}

/* Small screens: tighter layout, wrap controls */
@media (max-width: 768px) {
  .hikes-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
  .header-actions {
    margin-top: 1em;
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    justify-content: flex-start;
  }

  .filters-row {
    gap: 0.5rem;
    padding-bottom: 0.5rem;
  }
  .filters-row .btn-clear-all {
    flex: 0 0 auto;
    min-width: auto;
    padding: 0.5rem 0.75rem;
    font-size: 0.875rem;
  }

  .search-row {
    flex-direction: column;
    gap: 0.5rem;
  }
  .search-row .btn {
    width: auto;
    align-self: flex-start;
  }

  .hike-thumbnail-wrapper {
    height: 140px;
  }
  .hike-stats {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.5rem;
  }

  .see-more {
    justify-content: flex-start !important;
  }

  /* Adjust filter indicators for mobile */
  .filter-indicator {
    top: 4px;
    right: 4px;
    width: 5px;
    height: 5px;
  }
}

/* ---------------- Bulk Delete Action Bar ---------------- */
.bulk-action-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #1f2026;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.3);
  z-index: 1000;
  padding: 1rem 1.5rem;
}

.bulk-action-content {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.selection-info {
  font-size: 1rem;
  font-weight: 500;
  color: #fff;
}

.bulk-actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

/* Slide up transition for action bar */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

.slide-up-enter-to,
.slide-up-leave-from {
  transform: translateY(0);
  opacity: 1;
}

@media (max-width: 576px) {
  .bulk-action-bar {
    padding: 0.75rem 1rem;
  }

  .bulk-action-content {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }

  .selection-info {
    text-align: center;
    font-size: 0.875rem;
  }

  .bulk-actions {
    justify-content: center;
    flex-wrap: wrap;
  }
}
</style>
